import React, { useState, useEffect, useCallback } from "react";
import { api, API } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useModules } from "../contexts/ModulesContext";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Download, Eye, Info, Package } from "lucide-react";
import { toast } from "sonner";

const ALL_ENTITIES = [
  { key: "users", label: "Collaborateurs (users)", module: null },
  { key: "user-records", label: "Contrats (user-records)", module: null },
  { key: "org-structure", label: "Organisation complète (Directions + Services + Équipes)", module: null, bundle: true },
  { key: "pays", label: "Pays", module: "pays" },
  { key: "regions", label: "Régions", module: "regions" },
  { key: "sites", label: "Sites", module: "sites" },
  { key: "societes", label: "Sociétés", module: "societes" },
  { key: "directions", label: "Directions", module: "directions" },
  { key: "services", label: "Services", module: "services" },
  { key: "equipes", label: "Équipes", module: "equipes" },
  { key: "profiles", label: "Profils étendus", module: "profiles" },
];

export default function ExportPage() {
  const { t } = useI18n();
  const { isEnabled } = useModules();
  const [entity, setEntity] = useState("users");
  const [separator, setSeparator] = useState(";");
  const [bom, setBom] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const available = ALL_ENTITIES.filter((e) => !e.module || isEnabled(e.module));
  const multiRecord = isEnabled("multi_record");
  const showMainOnlyHint = entity === "user-records" && !multiRecord;

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/export/${entity}`, { params: { preview: true } });
      setPreview(data.data);
    } catch (e) {
      console.error("Export preview failed:", e);
      toast.error("Erreur lors de l'aperçu");
    } finally {
      setLoading(false);
    }
  }, [entity]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const download = () => {
    const token = localStorage.getItem("sirh_token") || "";
    const sep = separator === "tab" ? "\t" : separator;
    const url = `${API}/export/${entity}?separator=${encodeURIComponent(sep)}&bom=${bom}`;
    // use fetch to bypass cookie issues
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `SIRH_${entity}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Export téléchargé");
      })
      .catch(() => toast.error("Erreur de téléchargement"));
  };

  const downloadZip = () => {
    const token = localStorage.getItem("sirh_token") || "";
    const sep = separator === "tab" ? "\t" : separator;
    const url = `${API}/export-zip?separator=${encodeURIComponent(sep)}&bom=${bom}`;
    toast.info("Génération du snapshot…");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        const ts = new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "");
        a.download = `SIRH_snapshot_${ts}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Snapshot ZIP téléchargé");
      })
      .catch(() => toast.error("Erreur de téléchargement du snapshot"));
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="export-page">
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-warm-dark">{t("export")}</h1>
          <p className="text-sm text-warm-dark/60 mt-1">Export CSV, compatible Excel FR (UTF-8 BOM)</p>
        </div>
        <div className="text-right">
          <Button onClick={downloadZip} className="bg-sage hover:bg-sage/90 text-cream" data-testid="btn-download-zip">
            <Package size={14} className="mr-1.5" />Snapshot ZIP complet
          </Button>
          <div className="text-[11px] text-warm-dark/60 mt-1.5">Toutes les entités + manifeste, en un seul fichier</div>
        </div>
      </div>

      <div className="grid grid-cols-[380px_1fr] gap-4">
        <Card className="border-sand shadow-soft p-6 h-fit">
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">{t("entity")}</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger data-testid="export-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {available.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-warm-dark/80 mb-1.5 block">{t("separator")}</Label>
              <Select value={separator} onValueChange={setSeparator}>
                <SelectTrigger data-testid="export-sep"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=";">Point-virgule (;)</SelectItem>
                  <SelectItem value=",">Virgule (,)</SelectItem>
                  <SelectItem value="tab">Tabulation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs font-medium text-warm-dark/80 block">BOM UTF-8</Label>
                <div className="text-[11px] text-warm-dark/60">Recommandé pour Excel FR</div>
              </div>
              <Switch checked={bom} onCheckedChange={setBom} data-testid="export-bom" />
            </div>
            <div className="pt-3 space-y-2">
              {showMainOnlyHint && (
                <div className="flex items-start gap-2 text-[11px] text-warm-dark bg-sand/40 border border-sand rounded-md px-3 py-2" data-testid="export-mainonly-hint">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  <span>Mode MultiRecord désactivé — seules les fiches principales (<span className="font-semibold">is_main=true</span>) seront exportées, soit une fiche par utilisateur.</span>
                </div>
              )}
              {entity === "org-structure" && (
                <div className="flex items-start gap-2 text-[11px] text-warm-dark bg-sage-light/40 border border-sage/40 rounded-md px-3 py-2" data-testid="export-bundle-hint">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  <span>Export hiérarchique d'une seule traite : chaque ligne porte une colonne <span className="font-mono">level</span> (Direction / Service / Équipe) + libellés parent dénormalisés. Idéal pour mapper l'arborescence vers NIM.</span>
                </div>
              )}
              <Button variant="outline" onClick={loadPreview} className="w-full" data-testid="btn-preview"><Eye size={14} className="mr-1.5" />{t("export_preview")}</Button>
              <Button onClick={download} className="w-full bg-terracotta hover:bg-terracotta-dark" data-testid="btn-download"><Download size={14} className="mr-1.5" />{t("download")}</Button>
            </div>
          </div>
        </Card>

        <Card className="border-sand shadow-soft overflow-hidden">
          <div className="px-5 py-3 border-b border-sand bg-cream/50">
            <div className="text-xs font-medium uppercase tracking-wider text-warm-dark/60">{t("export_preview")}</div>
          </div>
          {loading ? <div className="p-10 text-warm-dark/50 text-sm">Chargement…</div> : preview ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream">
                  <tr>
                    {preview.headers.map((h) => (
                      <th key={h} className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wider text-warm-dark/60 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand">
                  {preview.rows.length === 0 ? (
                    <tr><td colSpan={preview.headers.length} className="text-center py-10 text-warm-dark/50">Aucune donnée</td></tr>
                  ) : preview.rows.map((r, i) => (
                    <tr key={r.id || `row-${i}`} data-testid={`preview-row-${i}`}>
                      {preview.headers.map((h) => <td key={h} className="px-4 py-2 text-warm-dark/80 mono text-xs whitespace-nowrap">{r[h] === null || r[h] === undefined ? "" : String(r[h])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="p-10 text-warm-dark/50 text-sm">—</div>}
        </Card>
      </div>
    </div>
  );
}
